// owner-map: OP-06.spa
import { patchForecast, putForecastNotify } from "../services/forecastRowApi.js";
import { beginPin, intentArgs } from "../lib/telemetry/uiIntents.js";
import { apiPut } from "../lib/apiClient.js";
import { createWriteIntents } from "../lib/telemetry/writeIntents.js";
import { useCallback, useEffect, useRef } from "react";
import { specToCadIdx, cadIdxToSpec, specToEngine } from "../lib/scheduleMap";
import {
  TRACKING_DEFAULTS,
  alertPrefsFromNotify,
  notifyPatchFromAlerts,
  soleWatcherSchedule,
} from "../lib/tracking.js";
import { notifyPatched, scheduleSet } from "../lib/forecast/reducer.js";
import { noteSharedAccessLoss } from "../lib/forecast/sharedAccessLoss.js";
import { toast } from "../lib/ui/toast.js";
import { ERROR_TEXT } from "../lib/forecast/runErrorText.js";
import { runStore } from "../store/runStore.js";

/** Records `revision` for `id` in `seen` and answers whether it is newer than any recorded. */
function takeNewer(seen, id, revision) {
  if (!Number.isInteger(revision) || revision <= (seen[id] ?? -1)) return false;
  seen[id] = revision;
  return true;
}

/**
 * The `revision` a schedule write's answer reports, or null: apiPut's parsed `body`, or the
 * owner PATCH's fetch Response read here.
 */
async function answerRevision(r, parsed) {
  if (parsed) return r.body?.revision ?? null;
  if (typeof r.json !== "function") return null;
  try {
    return (await r.json())?.revision ?? null;
  } catch (e) {
    console.warn("[applySchedule] unreadable schedule answer:", e);
    return null;
  }
}

/**
 * Tracking-prefs domain: getTrackingPrefs, applySchedule, patchTrackingPrefs.
 * Merges server-sourced cadence with localStorage alert prefs, owns all
 * schedule PATCH writes, and patches the local alert prefs store. Writes the run-store
 * singleton (`runStore.dispatch`): no store handles are threaded in.
 *
 * @param {{ forecastsById: Map, trackingPrefs: object, setTrackingPrefs: Function, pinnedRef: React.MutableRefObject, setPinned: Function, authEnabled: boolean }} args
 * @returns {{ getTrackingPrefs: Function, applySchedule: Function, patchTrackingPrefs: Function }}
 */
export function useTrackingPrefs({
  forecastsById,
  trackingPrefs,
  setTrackingPrefs,
  pinnedRef,
  setPinned,
  authEnabled,
  // Called when a schedule write is REFUSED, with the id and the response — the
  // out-of-org collaborator's `403 out_of_org` is the case that has a real UI
  // ("this one needs your own copy"). Optional: everything else here is
  // fire-and-forget, and a caller with nothing to say about a rejection should
  // not have to pass a no-op.
  onScheduleRejected,
  // Called when a write is refused BECAUSE the share is gone — the row has
  // already been dropped from the list by then; this is only how the user is
  // told. Optional, like onScheduleRejected.
  onSharedAccessLost,
}) {
  // Debounce the notify PATCH per forecast: the threshold slider fires onChange
  // on every pointer-move, so PATCHing on each would be a request per drag step.
  // Local state updates immediately (below); the server write coalesces to the
  // last value ~300ms after the user pauses. pending* accumulates fields so a
  // rapid email-toggle + threshold-drag still persists both.
  const patchTimersRef = useRef({});
  const pendingNotifyRef = useRef({});
  // The forecast_settings.change intents whose fields wait in pendingNotifyRef, per forecast:
  // the coalesced write carries them. writeIntents ends each with its last write's outcome
  // once its gesture is over and nothing of it is queued or in flight.
  const pendingIntentsRef = useRef({});
  const writeIntents = useRef(createWriteIntents()).current;
  // Every write that can change a schedule (watch, unwatch, a slider move) is answered with the
  // row's revision after it. Per forecast, an answer's schedule lands only when its revision is
  // newer than the last one landed, so answers that arrive out of order leave the newest.
  const scheduleRevisionRef = useRef({});
  // Clear any pending debounce timers on unmount so a queued PATCH can't fire
  // after the component is gone.
  useEffect(
    () => () => {
      for (const t of Object.values(patchTimersRef.current)) clearTimeout(t);
    },
    [],
  );
  // Returns the tracking shape for a forecast — merges saved prefs over
  // the defaults so a newly-pinned forecast renders with sensible values
  // (Events cadence, ±10% threshold, in-app on, email off) before the
  // user has touched the settings flyover.
  const getTrackingPrefs = useCallback(
    (fcId) => {
      const f = forecastsById.get(fcId);
      // Pass contractStatus so a terminal (deleted/resolved) contract reads as
      // None even if a stale schedule spec survives on the row (symptom A).
      const cadIdx = specToCadIdx(f && f.schedule, f && f.contractStatus); // server-sourced
      const engine = specToEngine(f && f.schedule); // server-sourced reforecast tier (quick/deep)
      const alerts = trackingPrefs[fcId] || {}; // localStorage: thrIdx/inapp/email
      // Server-persisted alert prefs ({email, thresholdPct, leadingBin}) win over
      // the local cache for the fields they own. `inapp` stays client-only. An
      // absent notify.leadingBin falls through to TRACKING_DEFAULTS (ON), mirroring
      // the server's leadingBinEnabled.
      const serverAlerts = alertPrefsFromNotify(f && f.notify);
      return { ...TRACKING_DEFAULTS, ...alerts, ...serverAlerts, cadIdx, engine }; // server + cadIdx/engine win over stale local
    },
    [forecastsById, trackingPrefs],
  );
  // ── WATCHLIST MEMBERSHIP, which is NOT the cadence ────────────────────────
  // Membership used to BE the cadence: "watchlisted" was derived from the
  // `schedule` column, so the only way onto a watchlist was to write a schedule
  // onto a row you own. The collaborative design makes the cadence SHARED (one
  // timeline every holder experiences) and the watchlist PERSONAL, which one
  // column cannot be — and it is why a recipient of a shared forecast had no eye
  // at all: the owner-scoped PATCH refused them.
  //
  // So the eye now writes per-user membership (`PUT /:id/watchlist`, authorized by
  // READABILITY rather than ownership) and the cadence slider keeps writing the
  // schedule. Two writers, because they are two facts.
  //
  // The cadence still FOLLOWS the eye, as a server rule applied under the forecast's lock
  // (server/handlers/watchlist.js): a watch starts an unscheduled forecast Weekly, and an
  // unwatch stops it once nobody is left watching — for whoever may edit the cadence. The
  // response carries the schedule as the server last saw it, which lands in the store here.
  //
  // `id` is the wire client_id, as everywhere else here; the endpoint needs the
  // server uuid, so it is read off the row.
  const toggleWatchlist = useCallback(
    (id, wanted, at) => {
      // Optimistic, and in lockstep with the synchronous mirror handlePin's cap
      // check reads — same contract applySchedule keeps.
      pinnedRef.current = { ...pinnedRef.current, [id]: wanted };
      setPinned((p) => ({ ...p, [id]: wanted }));
      // The user's pin / unpin (P2 design §4.6), ended with the write's outcome.
      const intent = beginPin(id, wanted, at);
      const row = forecastsById.get(id);
      const forecastId = row?.forecastId;
      // With sign-in off nothing is saved and this browser is the only watcher. A signed-in row
      // with no server id yet keeps its membership local and shows no schedule nothing will save.
      if (!authEnabled || !forecastId) {
        const spec = authEnabled ? null : soleWatcherSchedule(row, wanted);
        if (spec) runStore.dispatch(scheduleSet(id, spec));
        intent.end("ok");
        return;
      }
      // Through `apiPut`, not a raw fetch. The neighbouring applySchedule predates
      // the api client and still hand-rolls one; new writes go through the client
      // (which is also what the ratchet's `strayFetch` counter is protecting — it
      // caught this line as a regression when it was a bare fetch).
      apiPut(
        `/api/forecasts/${encodeURIComponent(forecastId)}/watchlist`,
        { watchlisted: wanted },
        ...intentArgs(intent),
      )
        .then((r) => {
          intent.end(r?.ok ? "ok" : "error");
          if (r?.ok && takeNewer(scheduleRevisionRef.current, id, r.body?.revision))
            runStore.dispatch(scheduleSet(id, r.body.schedule ?? null));
          // Optimistic, like every other write here: the UI is not rolled back on
          // failure, but a refusal is logged so an inconsistent pinned-vs-server
          // state stays diagnosable.
          if (!r?.ok) {
            console.warn(
              `[toggleWatchlist] PUT /api/forecasts/${forecastId}/watchlist failed: ${r?.status}`,
            );
            // A 404 on a SHARED row is the revocation discovering itself — see
            // sharedAccessLoss.js. The optimistic pin above is not rolled back
            // because the row it pinned is about to leave the list entirely.
            const lost = noteSharedAccessLoss({
              dispatch: runStore.dispatch,
              runId: id,
              status: r?.status,
              isShared: row?.isOwn === false,
            });
            if (lost) onSharedAccessLost?.(id, lost);
            else toast("Couldn't update your watchlist. Try again.", { kind: "error" });
          }
        })
        .catch((e) => {
          intent.end("error");
          console.warn(`[toggleWatchlist] PUT /api/forecasts/${forecastId}/watchlist errored:`, e);
          toast(ERROR_TEXT.offline, { kind: "error" });
        });
    },
    [authEnabled, forecastsById, pinnedRef, setPinned, onSharedAccessLost],
  );

  // Single source of schedule writes. Maps the cadence index to a spec, PATCHes
  // it once, and optimistically updates UI state: the forecast's schedule (which
  // drives the slider via specToCadIdx) and pinned (None=untracked, else
  // tracked). Used by the cadence slider and engine toggle (TrackingSettingsPanel)
  // so there is exactly one writer — no double-PATCH,
  // no redundant revisions. `id` is the wire client_id (forecast object's `id`,
  // pinned-map key, and the `:id` in the PATCH URL) — NOT the pg UUID.
  const applySchedule = useCallback(
    // `intent`: the forecast_settings.change that asked for it (open). The write sends its id
    // as `x-intent-id` and ends it with the write's outcome: a refused write, rolled back or
    // not, is an `error`; with no server write (auth off) the change is local and `ok`.
    (id, cadIdx, engine, { intent = null } = {}) => {
      const intentId = intent?.id || null;
      const settle = (outcome) => intent?.end?.(outcome);
      // Carry the reforecast engine tier through EVERY schedule write so cadence
      // and engine are independent: the cadence slider omits `engine` (preserve the
      // current one, read from the live spec), the engine toggle passes it (preserve
      // the current cadIdx). A freshly-pinned forecast with no prior spec defaults
      // to 'quick' via specToEngine.
      const row = forecastsById.get(id);
      const shared = row?.isOwn === false;
      // The spec to put back if the write is REFUSED. Captured before the
      // optimistic dispatch below, and only used on the shared route — see the
      // rejection handler for why the owner route stays optimistic-only.
      const prevSpec = row?.schedule ?? null;
      const eng = engine || specToEngine(row?.schedule);
      const spec = cadIdxToSpec(cadIdx, eng);
      runStore.dispatch(scheduleSet(id, spec)); // dual-write schedule stamp to the store
      // Keep the synchronous pinned mirror in lockstep on EVERY schedule write
      // (eye-icon AND slider), so handlePin's cap check reads an up-to-date count
      // even across rapid writes before React commits. cadIdx 0 = None = untracked.
      //
      // OWNER ROWS ONLY. On an owner's row the cadence and watchlist membership
      // are deliberately coupled — dropping your own forecast from the watchlist
      // stops reforecasting it. On a SHARED row they are not the same thing at
      // all: the cadence belongs to the question and every holder feels it, while
      // the watchlist is one person's list (its own table, written by
      // toggleWatchlist). Mirroring here would mean a colleague setting the
      // shared cadence to Never silently removed the forecast from their own
      // watchlist — a second, unasked-for effect from one control.
      if (!shared) {
        pinnedRef.current = { ...pinnedRef.current, [id]: cadIdx !== 0 };
        setPinned((p) => {
          // Explicit `false` tombstone on unpin (not delete): the initial-load
          // merge setPinned(prev=>({...pins,...prev})) needs a local key here to
          // override a stale server pin if the user unpins while that load is
          // still in flight. Consumers treat false the same as absent (unpinned).
          if (cadIdx === 0) return { ...p, [id]: false };
          return { ...p, [id]: true };
        });
      }
      if (!authEnabled) settle("ok");
      if (authEnabled) {
        // ── WHICH ROUTE, and why there are two ──────────────────────────────
        // The owner's PATCH is scoped `WHERE client_id = $1 AND user_id = $2`,
        // so a collaborator's write matches ZERO rows and silently does nothing.
        // A shared row therefore goes to `PUT /:id/cadence`, which authorizes on
        // readability + same organization instead of ownership.
        //
        // The two routes are keyed DIFFERENTLY, and this is the reason: the
        // owner's PATCH takes the wire `id` (client_id), which is unique only
        // WITHIN one user — the same client_id can exist in the viewer's own
        // space and in the owner's. The shared route takes the pg UUID
        // (`forecastId`), the only identifier that means the same forecast to
        // both people. Sending `id` there would address a different row or none.
        const request = shared
          ? row?.forecastId
            ? apiPut(
                `/api/forecasts/${encodeURIComponent(row.forecastId)}/cadence`,
                { schedule: spec },
                { intentId },
              )
            : // A shared row with no UUID cannot be addressed at all. Refusing
              // loudly beats falling back to the owner PATCH, which would return
              // a cheerful 200 having matched nothing.
              Promise.resolve({ ok: false, status: 0 })
          : // The owner path is UNCHANGED — the same bare PATCH it has always been.
            // Both branches settle to something with `{ok, status}` (a fetch
            // Response and apiPut's result agree on those two fields), so one
            // handler covers them.
            patchForecast(id, { schedule: spec, forecastId: row?.forecastId }, { intentId });
        // Optimistic (matches the content save-effect): we don't roll back the UI
        // on failure, but a non-2xx (e.g. 401 expired, 400 bad spec, 403
        // out_of_org) doesn't reject, so log it explicitly to keep an
        // inconsistent pinned/cadence vs. server state diagnosable.
        request
          .then((r) => {
            settle(r?.ok ? "ok" : "error");
            // A watchlist answer that arrived first may have replaced the optimistic value:
            // this write's schedule lands again when its revision is the newest.
            if (r.ok)
              return answerRevision(r, shared).then((revision) => {
                if (takeNewer(scheduleRevisionRef.current, id, revision))
                  runStore.dispatch(scheduleSet(id, spec));
              });
            if (!r.ok) {
              console.warn(
                `[applySchedule] ${shared ? "PUT" : "PATCH"} schedule for ${id} failed: ${r.status}`,
              );
              // ROLL THE SLIDER BACK, on the shared route only.
              //
              // Every other write here is optimistic-and-never-reverted, which is
              // right when the only realistic refusals are an expired session or a
              // bad spec — both of which mean the whole page is about to be
              // wrong anyway. A shared cadence has a refusal that is NORMAL and
              // specific: `403 out_of_org`. Leaving the slider where the user
              // dragged it would tell them the shared schedule changed when it
              // did not — the same lie as a control that silently does nothing,
              // just with extra steps. So the previous spec goes back.
              if (shared) runStore.dispatch(scheduleSet(id, prevSpec));
              // ...and if the refusal was a 404, the share itself is gone — the
              // roll-back above put the slider back on a row that is now leaving.
              const lost = noteSharedAccessLoss({
                dispatch: runStore.dispatch,
                runId: id,
                status: r?.status,
                isShared: shared,
              });
              if (lost) onSharedAccessLost?.(id, lost);
              else
                toast(
                  r?.status === 403
                    ? "Only the owner can change how often this forecast updates."
                    : "Couldn't change how often this forecast updates. Try again.",
                  { kind: "error" },
                );
              onScheduleRejected?.(id, r);
            }
          })
          .catch((e) => {
            settle("error");
            console.warn(`[applySchedule] schedule write for ${id} errored:`, e);
            toast(ERROR_TEXT.offline, { kind: "error" });
          });
      }
    },
    [authEnabled, pinnedRef, setPinned, forecastsById, onScheduleRejected, onSharedAccessLost],
  );
  const patchTrackingPrefs = useCallback(
    // `intent`: the forecast_settings.change behind this patch (open), ended with the outcome
    // of its last write; a client-only patch (no server field) ends it `ok`. `holding`: more of
    // the same gesture follows (a threshold drag); a `null` patch then says the gesture ended.
    (fcId, patch, { intent = null, holding = false } = {}) => {
      if (patch == null) {
        writeIntents.release(intent);
        return;
      }
      setTrackingPrefs((prev) => {
        const merged = { ...TRACKING_DEFAULTS, ...(prev[fcId] || {}), ...patch };
        return { ...prev, [fcId]: merged };
      });
      // Persist the server-owned alert fields (email opt-in, threshold) so alerts
      // fire from the notifier service, not just the browser. `inapp` is
      // client-only and never sent. Debounced + coalesced (see refs above);
      // optimistic (mirrors applySchedule): a non-2xx doesn't reject fetch, so log
      // it to keep drift diagnosable.
      const notifyPatch = notifyPatchFromAlerts(patch);
      const writes = !!(authEnabled && notifyPatch);
      writeIntents.change(intent, { holding, queued: writes });
      if (writes) {
        if (intent?.id) {
          // The latest change goes last, since the request names the last one's intent.
          const queued = (pendingIntentsRef.current[fcId] || []).filter((it) => it !== intent);
          pendingIntentsRef.current[fcId] = [...queued, intent];
        }
        // The store's run.notify is what getTrackingPrefs reads (and it wins over the
        // local cache), so it must move now, not after the debounced write or a reload.
        runStore.dispatch(notifyPatched(fcId, notifyPatch));
        const pend = { ...(pendingNotifyRef.current[fcId] || {}), ...notifyPatch };
        // Capture the reader's timezone so alert emails can render timestamps in
        // their local zone (the notifier falls back to UTC when absent). Sent on
        // the same notify PATCH; refreshed whenever they touch alert settings.
        try {
          const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (z) pend.timezone = z;
        } catch {
          /* Intl unavailable — leave tz unset (UTC) */
        }
        pendingNotifyRef.current[fcId] = pend;
        clearTimeout(patchTimersRef.current[fcId]);
        patchTimersRef.current[fcId] = setTimeout(() => {
          const notify = pendingNotifyRef.current[fcId];
          // One request, so one `x-intent-id`: the latest change's. Its answer counts for
          // every intent it carries (writeIntents decides when that is the last word).
          const carried = pendingIntentsRef.current[fcId] || [];
          delete pendingNotifyRef.current[fcId];
          delete pendingIntentsRef.current[fcId];
          delete patchTimersRef.current[fcId];
          const ticket = writeIntents.send(carried);
          const settle = (outcome) => writeIntents.settle(ticket, outcome);
          // Alerts have two homes, so the route is picked by ownership: an owner's live
          // on `forecasts.notify` (owner-scoped PATCH, keyed by client_id), a
          // non-owner holder's in `forecast_notify` (PUT /:id/notify, keyed by the
          // forecasts PK). Skip a shared row with no PK — it could only 404.
          const row = forecastsById.get(fcId);
          const shared = row?.isOwn === false;
          if (shared && !row?.forecastId) {
            settle("error");
            return;
          }
          const write = { notify, forecastId: row?.forecastId };
          const intentId = carried[carried.length - 1]?.id;
          // Names the request actually sent — a log that says PATCH while a PUT went out
          // sends whoever is debugging it to the wrong route and the wrong table.
          const route = shared ? `PUT notify ${row.forecastId}` : `PATCH ${fcId}`;
          (shared
            ? putForecastNotify(row.forecastId, write, { intentId })
            : patchForecast(fcId, write, { intentId })
          )
            .then((r) => {
              settle(r?.ok ? "ok" : "error");
              if (!r.ok) console.warn(`[patchTrackingPrefs] ${route} notify failed: ${r.status}`);
            })
            .catch((e) => {
              settle("error");
              console.warn(`[patchTrackingPrefs] ${route} notify errored:`, e);
            });
        }, 300);
      }
    },
    [authEnabled, setTrackingPrefs, forecastsById, writeIntents],
  );

  return { getTrackingPrefs, applySchedule, toggleWatchlist, patchTrackingPrefs };
}
